import type { RulesParams, RulesQueryInput } from '../-types';

/**
 * The four fields the rules list maps into its `ORDER BY`
 * (`SORT_COLUMNS_ENUM` in `request/rule.js`, mapped in `db/rules.js`).
 *
 * `updatedAt` is deliberately absent, and so is `version` — the list renders
 * both and neither is sortable, exactly as on the old page.
 */
const SORTABLE_FIELDS = new Set([
  'connectionType',
  'entityId',
  'certification',
  'createdAt',
]);

/**
 * The columns this list filters on.
 *
 * Each becomes `LOWER(col) LIKE '%value%'`, so they are all text matches.
 *
 * Deliberately absent: `rules`. The route's `FILTER_COLUMNS_ENUM` does declare
 * it — it matches against `rules.rules::text` — but the old page offered no
 * such filter, and this list is the old page's, not a wider one.
 */
const FILTERABLE_FIELDS = new Set([
  'connectionType',
  'entityId',
  'certification',
  'createdAt',
]);

/**
 * Turns table state into the endpoint's parameters.
 *
 * Single conversion point, as on the other management lists: the query key is
 * what this returns, so the route loader (reading the URL) and the page's hook
 * (reading `useTable`) only share a cache entry if both derive their params the
 * same way.
 *
 * Deliberately absent: `type`. The old page read a `type` query param off its
 * own URL and forwarded it on every list request. The route's querystring
 * never declared it and is `additionalProperties: false`, so ajv stripped it
 * before the handler ran; the handler destructures only `page, count, sort, q,
 * filter`. It filtered nothing there and would filter nothing here.
 *
 * Deliberately absent too: `q`. The schema *does* declare it (`maxLength: 255`)
 * and the handler *does* forward it as `filterParams.q`, but `parseFilter` in
 * `db/rules.js` reads `filterParams.filter` and nothing else — `q` is dropped
 * on the floor one layer below the handler. Wiring the search box to it would
 * have produced a box that quietly returns the unfiltered list, so the table
 * ships without one and the per-column filters do the work instead.
 */
export function toRulesParams(input: RulesQueryInput): RulesParams {
  const filter: Record<string, string> = {};
  for (const [key, values] of Object.entries(input.filters ?? {})) {
    if (FILTERABLE_FIELDS.has(key) && values && values.length > 0) {
      filter[key] = values.join(',');
    }
  }
  const hasFilter = Object.keys(filter).length > 0;

  // Dropped rather than forwarded: the route's `sort` is a regex over its enum,
  // so an unlisted field 400s the whole list instead of being ignored.
  const [field] = (input.sort ?? '').split(':');
  const sort = field && SORTABLE_FIELDS.has(field) ? input.sort : undefined;

  return {
    page: Math.max(0, input.page - 1),
    count: input.limit,
    ...(sort ? { sort } : {}),
    ...(hasFilter ? { filter: JSON.stringify(filter) } : {}),
  };
}

export function buildRulesQs(params: RulesParams): string {
  const qs = new URLSearchParams();
  qs.set('page', String(params.page));
  qs.set('count', String(params.count));
  if (params.sort) qs.set('sort', params.sort);
  if (params.filter) qs.set('filter', params.filter);
  return qs.toString();
}
